/**
 * The searchable Block Kit select (single or multi), like Slack's: a button showing the choice (or chips for a
 * multi select) opening a popover with a search field and the choices.
 *
 * - **People / conversations / channels** search the server a page at a time (core `KitPickerViewModel`, "Show
 *   more" for the next page); conversations include DMs and group DMs. Without the API (or outside a workspace) the
 *   cached workspace is listed instead (`selectOptions`).
 * - **External selects** ask the app through the server after `min_query_length` characters.
 * - **Option groups** of static selects show under their labels.
 */
import { BlockKit as BK, KitPickerViewModel, type KitContainer, type KitPickerRow, type RichTextContext } from "@bundle/core";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useOwned } from "../hooks/owned.ts";
import { WorkspaceContext } from "../context/AppContext.tsx";
import { useViewModel } from "../hooks/index.ts";
import { useT } from "../i18n/I18nProvider.tsx";
import { Button, Checkbox, Chip, Icon, Popover, Pressable, Spinner, Stack, Text, TextField } from "../primitives/index.ts";

type KitOptionNode = BK.KitOptionNode;

/** The choices from the cache (no server search): the element's options, or the workspace's people / conversations / public channels. */
export function selectOptions(e: BK.KitSelectNode, ctx: RichTextContext): KitPickerRow[] {
  switch (e.source) {
    case "static":
    case "external":
      return e.options;
    case "users":
      return Object.values(ctx.members)
        .filter((m) => !m.deactivated && (e.excludeBots !== true || m.kind !== "agent"))
        .map((m): KitPickerRow => ({ value: m.userId, label: m.displayName, kind: m.kind === "agent" ? "bot" : "user" }))
        .sort((a, b) => a.label.localeCompare(b.label));
    case "channels":
    case "conversations": {
      const kinds = e.source === "channels" ? ["public"] : (e.include ?? ["public", "private", "im", "mpim"]);
      const kindOf = { public: "public", private: "private", dm: "im", group_dm: "mpim" } as const;
      return Object.values(ctx.channels)
        .filter((c) => !c.archived && kinds.includes(kindOf[c.kind]))
        .map((c): KitPickerRow => {
          const kind = kindOf[c.kind];
          if (kind !== "im" && kind !== "mpim") return { value: c.id, label: c.name, kind };
          const others = (c.dmUserIds ?? []).filter((u) => u !== ctx.meId);
          const names = (others.length === 0 ? [ctx.meId] : others).map((u) => ctx.members[u]?.displayName ?? u);
          return { value: c.id, label: names.join(", "), kind };
        })
        .sort((a, b) => Number(a.kind === "im" || a.kind === "mpim") - Number(b.kind === "im" || b.kind === "mpim") || a.label.localeCompare(b.label));
    }
  }
}

function RowIcon({ kind }: { kind: KitPickerRow["kind"] }) {
  if (kind === undefined) return null;
  const name = kind === "public" ? "hash" : kind === "private" ? "lock" : kind === "mpim" ? "users" : "user";
  return <Icon name={name} size="icon.sm" color="text.secondary" />;
}

export type KitPickerProps = {
  node: BK.KitSelectNode;
  value: string[];
  onChange: (value: string[]) => void;
  placeholder: string;
  label: string;
  ctx: RichTextContext;
  blockId?: string;
  container?: KitContainer;
  disabled?: boolean;
  error?: string;
  testID: string;
};

export function KitPicker(p: KitPickerProps) {
  const t = useT();
  const ws = useContext(WorkspaceContext)?.workspace;
  const { node } = p;
  // One view model per element and container (an app updating the message / view gets a fresh search), owned while
  // mounted (useOwned: safe under StrictMode).
  const where = p.container === undefined ? "" : JSON.stringify(p.container);
  const key = `kitPicker:${ws?.wsId ?? "-"}:${where}:${p.blockId ?? ""}:${JSON.stringify(node)}`;
  const create = useCallback(
    () => {
      const made = ws === undefined || node.source === "static" ? undefined : new KitPickerViewModel(ws, node, p.blockId ?? "", where === "" ? undefined : (JSON.parse(where) as KitContainer));
      return { vm: made, dispose: () => made?.dispose() };
    },
    [ws, node, p.blockId, where],
  );
  const vm = useOwned(key, create).vm;
  const live = vm !== undefined && vm.available ? vm : undefined;
  const state = useViewModelOrNull(live);
  const [open, setOpen] = useState(false);
  const [localQuery, setLocalQuery] = useState("");
  const cached = useMemo(() => selectOptions(node, p.ctx), [node, p.ctx]);
  useEffect(() => {
    if (live !== undefined && p.value.length > 0) live.resolve(p.value);
  }, [live, p.value]);

  const query = state?.query ?? localQuery;
  const setQuery = (q: string) => (live !== undefined ? live.setQuery(q) : setLocalQuery(q));
  const match = (o: KitOptionNode) => o.label.toLowerCase().includes(query.trim().toLowerCase());
  const rows: KitPickerRow[] = live !== undefined ? (state?.options ?? []) : cached.filter(match);
  const groups = live !== undefined ? state?.groups : node.groups?.map((g) => ({ label: g.label, options: g.options.filter(match) })).filter((g) => g.options.length > 0);
  const labelOf = (v: string) => live?.labelOf(v) ?? cached.find((o) => o.value === v)?.label ?? p.ctx.members[v]?.displayName ?? (p.ctx.channels[v] !== undefined ? p.ctx.channels[v].name : v);

  const choose = (v: string) => {
    if (!node.multi) {
      p.onChange([v]);
      setOpen(false);
      return;
    }
    const on = !p.value.includes(v);
    if (on && node.maxSelected !== undefined && p.value.length >= node.maxSelected) return;
    p.onChange(on ? [...p.value, v] : p.value.filter((x) => x !== v));
  };
  const row = (o: KitPickerRow) =>
    node.multi ? (
      <Checkbox key={o.value} label={o.label} value={p.value.includes(o.value)} onChange={() => choose(o.value)} testID={`${p.testID}-option-${o.value}`} />
    ) : (
      <Pressable key={o.value} onPress={() => choose(o.value)} radius="sm" pressed={p.value[0] === o.value} accessibilityLabel={o.label} testID={`${p.testID}-option-${o.value}`}>
        <Stack direction="horizontal" gap="2" align="center" paddingX="2" paddingY="1.5">
          <RowIcon kind={o.kind} />
          <Text lines={1}>{o.label}</Text>
          {p.value[0] === o.value ? <Icon name="check" size="icon.sm" color="text.secondary" /> : null}
        </Stack>
      </Pressable>
    );

  const anchorLabel = node.multi ? (p.value.length === 0 ? p.placeholder : t("blockKit.selected", { count: p.value.length })) : p.value[0] === undefined ? p.placeholder : labelOf(p.value[0]);
  return (
    <Stack gap="1.5" testID={p.testID}>
      {node.multi && p.value.length > 0 ? (
        <Stack direction="horizontal" wrap gap="1">
          {p.value.map((v) => (
            <Chip key={v} label={labelOf(v)} onRemove={() => p.onChange(p.value.filter((x) => x !== v))} removeLabel={t("blockKit.remove", { name: labelOf(v) })} testID={`${p.testID}-chip-${v}`} />
          ))}
        </Stack>
      ) : null}
      <Popover
        open={open}
        onClose={() => setOpen(false)}
        side="bottom"
        align="start"
        width="picker"
        anchor={
          <Button
            label={anchorLabel}
            icon="chevronDown"
            size="sm"
            variant="secondary"
            disabled={p.disabled}
            aria-label={p.label}
            onPress={() => {
              if (!open) live?.start();
              setOpen(!open);
            }}
            testID={`${p.testID}-open`}
          />
        }
      >
        <Stack gap="2" padding="2">
          <TextField value={query} onChange={setQuery} accessibilityLabel={t("blockKit.search")} placeholder={t("blockKit.search")} leadingIcon="search" autoFocus testID={`${p.testID}-search`} />
          <Stack gap="0.5">
            {groups !== undefined
              ? groups.map((g) => (
                <Stack key={g.label} gap="0.5">
                  <Text variant="caption" weight="semibold" color="text.secondary">
                    {g.label}
                  </Text>
                  {g.options.map(row)}
                </Stack>
              ))
              : rows.map(row)}
            {state?.loading === true ? <Spinner size="icon.sm" /> : null}
            {state !== undefined && state.needsChars > 0 ? (
              <Text variant="footnote" color="text.secondary">
                {t("blockKit.typeMore", { count: state.needsChars })}
              </Text>
            ) : null}
            {state !== undefined && !state.loading && state.needsChars === 0 && rows.length === 0 ? (
              <Text variant="footnote" color="text.secondary">
                {state.failed ? t("blockKit.searchFailed") : t("blockKit.noMatches")}
              </Text>
            ) : null}
            {state?.hasMore === true ? <Button label={t("blockKit.showMore")} size="sm" variant="ghost" onPress={() => live?.loadMore()} testID={`${p.testID}-more`} /> : null}
          </Stack>
        </Stack>
      </Popover>
      {p.error === undefined ? null : (
        <Text variant="footnote" color="status.danger">
          {p.error}
        </Text>
      )}
    </Stack>
  );
}

const NONE = { subscribe: () => () => undefined, getSnapshot: () => undefined };
function useViewModelOrNull(vm: KitPickerViewModel | undefined) {
  return useViewModel((vm ?? NONE) as KitPickerViewModel) as ReturnType<KitPickerViewModel["getSnapshot"]> | undefined;
}
